import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, collectionGroup, query, where, onSnapshot, doc, updateDoc, Timestamp, getDocs, getDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { ReminderQueueTester as RQT } from '../utils/reminderQueueTester';
import { useAuth } from '../contexts/authContext';
import { toast, Toaster } from 'react-hot-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ProcessRemindersStatus from '../components/admin/ProcessRemindersStatus';

const ReminderQueueTester = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [queuedItems, setQueuedItems] = useState([]);
  const [activeItems, setActiveItems] = useState([]); // New: Active reminders (sent, snoozed)
  const [sentItems, setSentItems] = useState([]);
  const [disciples, setDisciples] = useState([]);
  const [selectedDisciple, setSelectedDisciple] = useState('');
  const [minutesFromNow, setMinutesFromNow] = useState(1);
  const [notificationType, setNotificationType] = useState('fcm');
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [clearLoading, setClearLoading] = useState(false);
  
  // New diagnostic report state
  const [diagnosticReport, setDiagnosticReport] = useState([]);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState(60);

  // New: Diagnostic report for selected disciple
  const [discipleReport, setDiscipleReport] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);

  // Initialize Auto Reminders state
  const [selectedDiscipleForInit, setSelectedDiscipleForInit] = useState('');
  const [nextReminderDate, setNextReminderDate] = useState('');
  const [nextReminderTime, setNextReminderTime] = useState('09:00');
  const [initLoading, setInitLoading] = useState(false);
  const [initResult, setInitResult] = useState('');

  // Manual Queued Reminder Creation state
  const [manualTriggerLoading, setManualTriggerLoading] = useState(false);
  const [manualTriggerResult, setManualTriggerResult] = useState('');

  // Backfill Active Disciples Index state
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState('');

  // Simple function to fetch all reminders for all users
  const fetchAllReminders = async () => {
    try {
      const allReminders = [];
      
      // Get all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      
      for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        const remindersSnapshot = await getDocs(collection(db, 'users', userId, 'reminderQueue'));
        
        remindersSnapshot.docs.forEach(reminderDoc => {
          const data = reminderDoc.data();
          allReminders.push({
            id: reminderDoc.id,
            userId: userId,
            ...data,
            scheduledFor: data.scheduledFor?.toDate(),
            sentAt: data.sentAt?.toDate(),
            createdAt: data.createdAt?.toDate(),
            completedAt: data.completedAt?.toDate(),
            cancelledAt: data.cancelledAt?.toDate()
          });
        });
      }
      
      // Sort into categories
      const queued = allReminders.filter(r => r.reminderStatus === 'scheduled' && !r.archived);
      const active = allReminders.filter(r => ['sent', 'snoozed'].includes(r.reminderStatus) && !r.archived);
      const completed = allReminders.filter(r => ['completed', 'superseded', 'cancelled', 'expired'].includes(r.reminderStatus) && !r.archived);
      
      setQueuedItems(queued);
      setActiveItems(active);
      setSentItems(completed);
      
    } catch (error) {
      console.error('Error fetching all reminders:', error);
    }
  };

  useEffect(() => {
    if (!currentUser) return;

    const fetchDisciples = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!userDoc.exists()) {
            setDisciples([]);
            return;
        };

        const discipleIds = userDoc.data().discipleIds || [];
        if (discipleIds.length === 0) {
            setDisciples([]);
            return;
        };

        const disciplesPromises = discipleIds.map(id => 
          getDoc(doc(db, 'disciples', id))
        );
        
        const discipleDocs = await Promise.all(disciplesPromises);
        
        const disciplesList = discipleDocs
          .filter(doc => doc.exists())
          .map(doc => ({
            id: doc.id,
            name: doc.data().name || 'Unnamed Disciple'
          }));
        
        setDisciples(disciplesList);
      } catch (error) {
        console.error('Error fetching disciples:', error);
        toast.error('Error fetching disciples.');
      }
    };
    
    fetchDisciples();

    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setNextReminderDate(tomorrow.toISOString().split('T')[0]);

    const reminderQueueRef = collection(db, 'users', currentUser.uid, 'reminderQueue');

    const setupListener = (status, setter) => {
        const q = query(
            reminderQueueRef,
            where('queueStatus', '==', status)
        );
        return onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(d => ({
              id: d.id,
              ...d.data(),
              scheduledFor: d.data().scheduledFor?.toDate(),
              sentAt: d.data().sentAt?.toDate(),
              createdAt: d.data().createdAt?.toDate(),
              completedAt: d.data().completedAt?.toDate(),
              cancelledAt: d.data().cancelledAt?.toDate()
            }));
            setter(items);
        });
    };

    // Setup listeners for three-column approach: Queued, Active, Completed
    // Queued reminders: scheduled status, not archived
    const queuedQuery = query(
        reminderQueueRef,
        where('reminderStatus', '==', 'scheduled'),
        where('archived', '==', false)
    );

    const unsubscribeQueued = onSnapshot(queuedQuery, (snapshot) => {
        const items = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
          scheduledFor: d.data().scheduledFor?.toDate(),
          sentAt: d.data().sentAt?.toDate(),
          createdAt: d.data().createdAt?.toDate()
        }));
        setQueuedItems(items);
    });

    // Active reminders: sent or snoozed status, not archived
    const activeQuery = query(
        reminderQueueRef,
        where('reminderStatus', 'in', ['sent', 'snoozed']),
        where('archived', '==', false)
    );

    const unsubscribeActive = onSnapshot(activeQuery, (snapshot) => {
        const items = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
          scheduledFor: d.data().scheduledFor?.toDate(),
          sentAt: d.data().sentAt?.toDate(),
          snoozedAt: d.data().snoozedAt?.toDate(),
          snoozedUntil: d.data().snoozedUntil?.toDate(),
          createdAt: d.data().createdAt?.toDate()
        }));
        setActiveItems(items);
    });
    
    // Completed reminders: completed, superseded, cancelled, or expired status
    const completedQuery = query(
        reminderQueueRef,
        where('reminderStatus', 'in', ['completed', 'superseded', 'cancelled', 'expired']),
        where('archived', '==', false)
    );

    const unsubscribeCompleted = onSnapshot(completedQuery, (snapshot) => {
        const items = snapshot.docs.map(d => ({
            id: d.id,
            ...d.data(),
            scheduledFor: d.data().scheduledFor?.toDate(),
            sentAt: d.data().sentAt?.toDate(),
            completedAt: d.data().completedAt?.toDate(),
            cancelledAt: d.data().cancelledAt?.toDate(),
            createdAt: d.data().createdAt?.toDate(),
            archivedAt: d.data().archivedAt?.toDate()
        }));
        setSentItems(items);
    });

    return () => {
      unsubscribeQueued();
      unsubscribeActive();
      unsubscribeCompleted();
    };
  }, [currentUser]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        const users = usersSnap.docs.map(doc => ({
          uid: doc.id,
          appID: doc.data().appID || doc.id
        }));
        setAllUsers(users);
      } catch (err) {
        toast.error('Failed to load users for UID dropdown');
      }
    };
    fetchUsers();
  }, []);

  const handleCancelReminder = async (reminderId) => {
    if (!currentUser) return;
    const reminderRef = doc(db, 'users', currentUser.uid, 'reminderQueue', reminderId);
    const timestamp = Timestamp.now();
    await updateDoc(reminderRef, {
      queueStatus: 'completed',
      reminderStatus: 'cancelled',
      completionReason: 'mentor_cancelled',
      cancelledAt: timestamp,
      completedAt: timestamp,
      archived: true,
      archivedAt: timestamp
    });
    toast.success('Reminder cancelled.');
  };

  const handleAddTestReminder = async () => {
    if (!selectedDisciple) {
      toast.error('Please select a disciple first');
      return;
    }

    if (!currentUser) return;

    try {
      const disciple = disciples.find(d => d.id === selectedDisciple);

      // No need to fetch frequency here; addTestReminder now handles it
      await RQT.addTestReminder(
        currentUser.uid,
        disciple.id,
        disciple.name,
        minutesFromNow
      );
      toast.success('Test reminder added successfully');
    } catch (error) {
      console.error('Error adding test reminder:', error);
      toast.error('Failed to add test reminder');
    }
  };
  
  const handleTestNotification = async (reminderId) => {
    if (!currentUser) return;
    try {
      await RQT.testNotification(currentUser.uid, reminderId, notificationType);
      toast.success(`Test ${notificationType.toUpperCase()} notification sent!`);
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error(`Failed to send test notification: ${error.message}`);
    }
  };

  const handleClearReminders = async () => {
    if (!selectedUserId) {
      toast.error('Please select a UID to clear reminders');
      return;
    }
    const user = allUsers.find(u => u.appID === selectedUserId);
    if (!user) {
      toast.error('Invalid UID selected');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ALL reminders for user ${user.appID}? This cannot be undone.`)) {
      return;
    }
    setClearLoading(true);
    try {
      const reminderQueueRef = collection(db, 'users', user.uid, 'reminderQueue');
      const snap = await getDocs(reminderQueueRef);
      const batchDeletes = snap.docs.map(docSnap => deleteDoc(docSnap.ref));
      await Promise.all(batchDeletes);
      toast.success(`All reminders cleared for ${user.appID}`);
    } catch (err) {
      toast.error('Failed to clear reminders');
    } finally {
      setClearLoading(false);
    }
  };

  // New function to generate diagnostic report
  const handleGenerateDiagnosticReport = async () => {
    if (!currentUser) return;
    
    setGeneratingReport(true);
    try {
      const functions = getFunctions();
      const generateDiagnostics = httpsCallable(functions, 'generateFullReminderDiagnostics');
      const result = await generateDiagnostics({ intervalMinutes });
      setDiagnosticReport(result.data.diagnostics || []);
      toast.success('Diagnostic report generated successfully');
    } catch (error) {
      console.error('Error generating diagnostic report:', error);
      toast.error('Failed to generate diagnostic report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleGenerateDiscipleReport = async () => {
    console.log('🔍 === STARTING DISCIPLE DIAGNOSTIC REPORT ===');
    console.log('Current user:', currentUser?.uid);
    console.log('Selected disciple:', selectedDisciple);
    
    if (!currentUser || !selectedDisciple) {
      console.log('❌ Missing currentUser or selectedDisciple, aborting');
      return;
    }
    
    setReportLoading(true);
    try {
      // Step 1: Find disciple object
      const discipleObj = disciples.find(d => d.id === selectedDisciple);
      console.log('📋 Step 1 - Found disciple object:', discipleObj);
      
      // Step 2: Set up Firestore query
      const reminderQueueRef = collection(db, 'users', currentUser.uid, 'reminderQueue');
      const q = query(
        reminderQueueRef,
        where('discipleId', '==', selectedDisciple),
        orderBy('createdAt', 'desc')
      );
      console.log('🔍 Step 2 - Firestore query:', q);
      
      // Step 3: Execute query
      const snap = await getDocs(q);
      console.log('📊 Step 3 - Query results:');
      console.log('  - Total docs found:', snap.docs.length);
      console.log('  - Doc IDs:', snap.docs.map(doc => doc.id));
      
      // Step 4: Fetch mentorDetails
      console.log('👤 Step 4 - Fetching mentorDetails...');
      let mentorFrequency = null;
      let mentorLastReminderSent = null;
      try {
        const mentorRef = doc(db, 'disciples', selectedDisciple, 'mentorDetails', currentUser.uid);
        console.log('  - MentorDetails ref path:', mentorRef.path);
        
        const mentorDoc = await getDoc(mentorRef);
        console.log('  - MentorDetails exists:', mentorDoc.exists());
        
        if (mentorDoc.exists()) {
          const mentorData = mentorDoc.data();
          console.log('  - Full mentorData:', mentorData);
          console.log('  - reminderSettings:', mentorData?.reminderSettings);
          
          mentorFrequency = mentorData?.reminderSettings?.frequency;
          mentorLastReminderSent = mentorData?.reminderSettings?.lastReminderSent;
          
          console.log('  - Extracted frequency:', mentorFrequency);
          console.log('  - Extracted lastReminderSent:', mentorLastReminderSent);
          console.log('  - lastReminderSent type:', typeof mentorLastReminderSent);
          console.log('  - lastReminderSent has toDate:', mentorLastReminderSent?.toDate ? 'YES' : 'NO');
        } else {
          console.log('❌ No mentorDetails found for disciple:', selectedDisciple);
        }
      } catch (freqErr) {
        console.error('❌ Error fetching mentorDetails:', freqErr);
      }
      
      // Step 5: Process each reminder
      console.log('📝 Step 5 - Processing reminders...');
      const reminders = snap.docs.slice(0, 10).map((docSnap, index) => {
        console.log(`\n📋 Processing reminder ${index + 1}/${Math.min(snap.docs.length, 10)}:`);
        console.log('  - Doc ID:', docSnap.id);
        
        const d = docSnap.data();
        console.log('  - Raw reminder data:', d);
        
        // Calculate next reminder date
        console.log('  - Calculating next reminder date...');
        let nextReminder = 'N/A';
        console.log('    - d.lastReminderSent:', d.lastReminderSent);
        console.log('    - d.interval:', d.interval);
        console.log('    - d.reminderStatus:', d.reminderStatus);
        console.log('    - d.completedAt:', d.completedAt);
        console.log('    - d.sentAt:', d.sentAt);
        
        if (d.lastReminderSent && d.interval) {
          const base = d.lastReminderSent.toDate ? d.lastReminderSent.toDate() : d.lastReminderSent;
          nextReminder = new Date(base.getTime() + (d.interval * 24 * 60 * 60 * 1000)).toLocaleString();
          console.log('    - Using lastReminderSent for calculation');
        } else if (d.reminderStatus === 'completed' && d.completedAt && d.interval) {
          const base = d.completedAt.toDate ? d.completedAt.toDate() : d.completedAt;
          nextReminder = new Date(base.getTime() + (d.interval * 24 * 60 * 60 * 1000)).toLocaleString();
          console.log('    - Using completedAt for calculation (fallback)');
        } else if (d.reminderStatus === 'sent' && d.sentAt && d.interval) {
          const base = d.sentAt.toDate ? d.sentAt.toDate() : d.sentAt;
          nextReminder = new Date(base.getTime() + (d.interval * 24 * 60 * 60 * 1000)).toLocaleString();
          console.log('    - Using sentAt for calculation (fallback)');
        } else {
          console.log('    - No valid date found for calculation');
        }
        console.log('    - Calculated nextReminder:', nextReminder);
        
        // Notification sent checks
        console.log('  - Checking notification status...');
        const fcmSent = d.messageType && d.messageType.includes('push') && d.messageSent ? 'Y' : 'N';
        const emailSent = d.messageType && d.messageType.includes('email') && d.messageSent ? 'Y' : 'N';
        console.log('    - messageType:', d.messageType);
        console.log('    - messageSent:', d.messageSent);
        console.log('    - fcmSent:', fcmSent);
        console.log('    - emailSent:', emailSent);
        
        // Frequency at creation
        const frequency_at_creation = d.frequency_at_creation ?? d.interval ?? '';
        console.log('  - frequency_at_creation:', frequency_at_creation);
        
        // Format lastReminderSent date
        console.log('  - Formatting lastReminderDate...');
        let lastReminderDate = 'N/A';
        console.log('    - d.lastReminderSent:', d.lastReminderSent);
        console.log('    - mentorLastReminderSent:', mentorLastReminderSent);
        
        if (d.lastReminderSent) {
          const lastReminderTimestamp = d.lastReminderSent.toDate ? d.lastReminderSent.toDate() : d.lastReminderSent;
          lastReminderDate = lastReminderTimestamp.toLocaleString();
          console.log('    - Using reminder lastReminderSent');
        } else if (mentorLastReminderSent) {
          const lastReminderTimestamp = mentorLastReminderSent.toDate ? mentorLastReminderSent.toDate() : mentorLastReminderSent;
          lastReminderDate = lastReminderTimestamp.toLocaleString() + ' (from mentorDetails)';
          console.log('    - Using mentorDetails lastReminderSent (fallback)');
        } else {
          console.log('    - No lastReminderSent found anywhere');
        }
        console.log('    - Final lastReminderDate:', lastReminderDate);
        
        const result = {
          id: docSnap.id,
          reminderStatus: d.reminderStatus || '',
          queueStatus: d.queueStatus || '',
          completionReason: d.completionReason || '',
          lastReminderDate,
          nextReminder,
          fcmSent,
          emailSent,
          frequency_at_creation
        };
        
        console.log('  - Final processed reminder:', result);
        return result;
      });
      
      console.log('\n📊 Step 6 - Final processed reminders array:', reminders);
      setDiscipleReport(reminders);
      console.log('✅ Disciple diagnostic report completed successfully');
      
    } catch (err) {
      console.error('❌ Failed to generate disciple report:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        stack: err.stack
      });
      toast.error('Failed to generate disciple report');
    } finally {
      setReportLoading(false);
      console.log('🔚 Report loading state set to false');
    }
  };

  // Manual trigger for queued reminder creation
  const handleManualCreateQueuedReminders = async () => {
    if (!currentUser) return;
    
    setManualTriggerLoading(true);
    setManualTriggerResult('');
    
    try {
      const functions = getFunctions();
      const manualCreateQueuedReminders = httpsCallable(functions, 'manualCreateQueuedReminders');
      
      const result = await manualCreateQueuedReminders();
      const data = result.data;
      
      if (data.success) {
        setManualTriggerResult(`✅ Success! Created ${data.created} reminders, skipped ${data.skipped} out of ${data.total} total mentorDetails.`);
        toast.success(`Created ${data.created} queued reminders`);
      } else {
        setManualTriggerResult('❌ Failed to create queued reminders');
        toast.error('Failed to create queued reminders');
      }
    } catch (error) {
      console.error('Error triggering manual queued reminder creation:', error);
      setManualTriggerResult(`❌ Error: ${error.message}`);
      toast.error('Error triggering manual queued reminder creation');
    } finally {
      setManualTriggerLoading(false);
    }
  };

  // Backfill active disciples index
  const handleBackfillActiveDisciplesIndex = async () => {
    if (!currentUser) return;
    
    setBackfillLoading(true);
    setBackfillResult('');
    
    try {
      const functions = getFunctions();
      const backfillActiveDisciplesIndex = httpsCallable(functions, 'backfillActiveDisciplesIndex');
      
      const result = await backfillActiveDisciplesIndex();
      const data = result.data;
      
      if (data.success) {
        setBackfillResult(`✅ Success! Processed ${data.totalProcessed} disciples, ${data.activeDisciples} are active.`);
        toast.success(`Backfill completed: ${data.activeDisciples} active disciples`);
      } else {
        setBackfillResult('❌ Failed to backfill active disciples index');
        toast.error('Failed to backfill active disciples index');
      }
    } catch (error) {
      console.error('Error triggering backfill:', error);
      setBackfillResult(`❌ Error: ${error.message}`);
      toast.error('Error triggering backfill');
    } finally {
      setBackfillLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Toaster position="top-right" />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Reminder Queue Tester</h1>
          <p className="text-gray-600">Test the reminder queue functionality for the logged-in admin.</p>
        </div>
        <button
          onClick={() => navigate('/reminders-overview')}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap"
        >
          View Reminders Overview
        </button>
      </div>

      {/* Process Reminders Function Status */}
      <ProcessRemindersStatus />

      {/* Move: Disciple Diagnostic Report Section to Top */}
      <div className="mb-8 p-6 bg-green-50 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Disciple Diagnostic Report</h2>
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
          <select
            value={selectedDisciple}
            onChange={(e) => setSelectedDisciple(e.target.value)}
            className="flex-grow p-2 border rounded text-sm"
          >
            <option value="">Select a Disciple</option>
            {disciples.map(disciple => (
              <option key={disciple.id} value={disciple.id}>{disciple.name}</option>
            ))}
          </select>
          <button
            onClick={handleGenerateDiscipleReport}
            className="bg-green-600 text-white px-6 py-2 rounded-md font-semibold hover:bg-green-700 text-sm whitespace-nowrap"
            disabled={!selectedDisciple || reportLoading}
          >
            {reportLoading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
        {discipleReport.length > 0 && (
          <div className="bg-white rounded-lg shadow-md mt-4 overflow-x-auto">
            <h3 className="text-lg font-semibold mb-4 p-4 border-b">Reminder Diagnostic Report</h3>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">reminderStatus</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">queueStatus</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">completionReason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency at Creation (days)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Reminder Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Reminder will be</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">FCM sent?</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email sent?</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {discipleReport.map((row, idx) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{row.reminderStatus}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.queueStatus}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.completionReason}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.frequency_at_creation}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.lastReminderDate}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.nextReminder}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.fcmSent}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.emailSent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Initialize Auto Reminders Section */}
      <div className="mb-8 p-6 bg-purple-50 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Initialize Auto Reminders</h2>
        <p className="text-gray-700 mb-4">
          Select a disciple and set a future date/time for their next auto reminder.
          This will create a new reminder in their queue.
        </p>
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
          <select
            value={selectedDiscipleForInit}
            onChange={(e) => setSelectedDiscipleForInit(e.target.value)}
            className="flex-grow p-2 border rounded text-sm"
          >
            <option value="">Select a Disciple</option>
            {disciples.map(disciple => (
              <option key={disciple.id} value={disciple.id}>{disciple.name}</option>
            ))}
          </select>
          <input
            type="date"
            value={nextReminderDate}
            onChange={(e) => setNextReminderDate(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
          />
          <input
            type="time"
            value={nextReminderTime}
            onChange={(e) => setNextReminderTime(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
          />
          <button
            onClick={async () => {
              if (!selectedDiscipleForInit) {
                toast.error('Please select a disciple first');
                return;
              }
              if (!nextReminderDate || !nextReminderTime) {
                toast.error('Please select both date and time for the next reminder');
                return;
              }
              setInitLoading(true);
              try {
                const disciple = disciples.find(d => d.id === selectedDiscipleForInit);
                const nextReminderTimestamp = new Date(`${nextReminderDate} ${nextReminderTime}`).toISOString();
                await RQT.initializeAutoReminder(currentUser.uid, disciple.id, disciple.name, nextReminderTimestamp);
                setInitResult('Reminder initialized successfully!');
                toast.success('Reminder initialized successfully!');
              } catch (error) {
                console.error('Error initializing auto reminder:', error);
                setInitResult(`Failed to initialize auto reminder: ${error.message}`);
                toast.error(`Failed to initialize auto reminder: ${error.message}`);
              } finally {
                setInitLoading(false);
              }
            }}
            disabled={initLoading || !selectedDiscipleForInit || !nextReminderDate || !nextReminderTime}
            className="px-6 py-2 bg-purple-600 text-white font-semibold rounded-md shadow-sm hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {initLoading ? 'Initializing...' : 'Initialize Reminder'}
          </button>
        </div>
        {initResult && (
          <div className={`mt-4 p-3 rounded-md ${
            initResult.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {initResult}
          </div>
        )}
      </div>

      {/* Manual Queued Reminder Creation Section */}
      <div className="mb-8 p-6 bg-orange-50 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Manual Queued Reminder Creation</h2>
        <p className="text-gray-700 mb-4">
          This function scans all mentorDetails and creates queued reminders for disciples who need them.
          It runs automatically at 1 AM and 1 PM daily, but you can trigger it manually here.
        </p>
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleManualCreateQueuedReminders}
            disabled={manualTriggerLoading}
            className="px-6 py-2 bg-orange-600 text-white font-semibold rounded-md shadow-sm hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {manualTriggerLoading ? 'Creating...' : 'Create Queued Reminders'}
          </button>
        </div>
        {manualTriggerResult && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-blue-800 text-sm">{manualTriggerResult}</p>
          </div>
        )}
      </div>

      {/* Backfill Active Disciples Index Section */}
      <div className="mb-8 p-6 bg-yellow-50 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Backfill Active Disciples Index</h2>
        <p className="text-gray-700 mb-4">
          This function creates an index of all disciples who are active for at least one user.
          This improves performance for the reminder system. Run this once to populate the index.
        </p>
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleBackfillActiveDisciplesIndex}
            disabled={backfillLoading}
            className="px-6 py-2 bg-yellow-600 text-white font-semibold rounded-md shadow-sm hover:bg-yellow-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {backfillLoading ? 'Backfilling...' : 'Backfill Active Disciples Index'}
          </button>
        </div>
        {backfillResult && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-blue-800 text-sm">{backfillResult}</p>
          </div>
        )}
      </div>

      {/* New Diagnostic Report Section */}
      <div className="mb-8 p-6 bg-blue-50 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Comprehensive Reminder Diagnostics</h2>
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <label htmlFor="interval" className="text-sm font-medium text-gray-700">Minutes Between Reminders (test):</label>
            <input
              id="interval"
              type="number"
              min={1}
              value={intervalMinutes}
              onChange={e => setIntervalMinutes(Number(e.target.value))}
              className="w-24 p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleGenerateDiagnosticReport}
            disabled={generatingReport}
            className="px-6 py-2 bg-green-600 text-white font-semibold rounded-md shadow-sm hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {generatingReport ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
        
        {diagnosticReport.length > 0 && (
          <div className="bg-white rounded-lg shadow-md mt-4 overflow-x-auto">
            <h3 className="text-lg font-semibold mb-4 p-4 border-b">Full Reminder Report</h3>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disciple Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reminder Frequency</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Reminder</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status of Last Reminder</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Reminder</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status of Next Reminder</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notification Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Archived</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {diagnosticReport.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.userName}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.discipleName}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.reminderFrequency}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.lastReminder}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.lastReminderStatus}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.nextReminder}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.nextReminderStatus}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.notificationStatus}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        row.archived ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {row.archived ? 'Yes' : 'No'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mb-6 flex flex-col sm:flex-row items-center gap-3">
        <select
          value={selectedUserId}
          onChange={e => setSelectedUserId(e.target.value)}
          className="p-2 border rounded text-sm w-full sm:w-auto"
        >
          <option value="">Select UID (appID)</option>
          {allUsers.map(user => (
            <option key={user.uid} value={user.appID}>{user.appID}</option>
          ))}
        </select>
        <button
          onClick={handleClearReminders}
          className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 text-sm font-semibold disabled:opacity-60"
          disabled={clearLoading || !selectedUserId}
        >
          {clearLoading ? 'Clearing...' : 'Clear Reminders'}
        </button>
      </div>

      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <h2 className="text-base sm:text-lg font-semibold mb-3">Add Test Reminder</h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={selectedDisciple}
            onChange={(e) => setSelectedDisciple(e.target.value)}
            className="flex-grow p-2 border rounded text-sm"
          >
            <option value="">Select a Disciple</option>
            {disciples.map(disciple => (
              <option key={disciple.id} value={disciple.id}>{disciple.name}</option>
            ))}
          </select>
            <input
              type="number"
              value={minutesFromNow}
              onChange={(e) => setMinutesFromNow(parseInt(e.target.value))}
              className="w-20 border p-2 rounded text-sm"
              min="1"
              placeholder="Minutes"
            />
            <button
              onClick={handleAddTestReminder}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm whitespace-nowrap"
            >
              Add Reminder
            </button>
          <button
            onClick={async () => {
              if (!selectedDisciple) {
                toast.error('Please select a disciple first');
                return;
              }
              const disciple = disciples.find(d => d.id === selectedDisciple);
              try {
                await RQT.testCloudFunctionProcessing(currentUser.uid, disciple.id, disciple.name, minutesFromNow);
                toast.success('Cloud function test initiated. Check console for results.');
              } catch (error) {
                toast.error('Failed to test cloud function');
              }
            }}
            className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 text-sm whitespace-nowrap"
          >
            Test Cloud Function
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {/* Queued Reminders Column */}
        <div>
          <h2 className="text-base sm:text-lg font-semibold mb-3">Queued Reminders</h2>
          <div className="space-y-2">
            {queuedItems.length === 0 ? (
              <p className="text-gray-500 p-3 bg-gray-50 rounded text-sm">No queued reminders</p>
            ) : (
              queuedItems.map(item => (
                <div key={item.id} className="border rounded bg-blue-50 p-3 shadow-sm">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium text-sm sm:text-base truncate">{item.discipleName}</h3>
                    <span className="ml-2 text-xs text-gray-500 whitespace-nowrap">
                      {item.scheduledFor?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mb-2">
                    Mentor: {item.mentorName || item.mentorId || 'Unknown'}
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="font-semibold text-blue-600">
                        {item.reminderStatus}
                      </span>
                      <span>•</span>
                      <span>{item.queueStatus}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleCancelReminder(item.id)}
                        className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={() => handleTestNotification(item.id)}
                        className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                      >
                        Test
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Active Reminders Column */}
        <div>
          <h2 className="text-base sm:text-lg font-semibold mb-3">Active Reminders</h2>
          <div className="space-y-2">
            {activeItems.length === 0 ? (
              <p className="text-gray-500 p-3 bg-gray-50 rounded text-sm">No active reminders</p>
            ) : (
              activeItems.map(item => (
                <div key={item.id} className="border rounded bg-white p-3 shadow-sm">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium text-sm sm:text-base truncate flex-1 min-w-0">{item.discipleName}</h3>
                    <span className="ml-2 text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                      {item.scheduledFor?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mb-2 truncate">
                    Mentor: {item.mentorName || item.mentorId || 'Unknown'}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                    <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                      <span className={`font-semibold ${
                        item.reminderStatus === 'sent' ? 'text-blue-600' : 
                        item.reminderStatus === 'snoozed' ? 'text-yellow-600' : 
                        'text-gray-600'
                      }`}>
                        {item.reminderStatus}
                      </span>
                      <span>•</span>
                      <span>{item.queueStatus}</span>
                      {item.messageType && (
                        <>
                          <span>•</span>
                          <span className="truncate">{item.messageType}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <select
                        value={notificationType}
                        onChange={(e) => setNotificationType(e.target.value)}
                        className="text-xs border rounded px-1.5 py-1 min-w-0 flex-shrink"
                      >
                        <option value="email">Email</option>
                        <option value="fcm">FCM Push</option>
                        <option value="both">Both (Email + FCM)</option>
                      </select>
                      <button 
                        onClick={() => handleCancelReminder(item.id)}
                        className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 whitespace-nowrap flex-shrink-0"
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={() => handleTestNotification(item.id)}
                        className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600 whitespace-nowrap flex-shrink-0"
                      >
                        Test
                      </button>
                    </div>
                  </div>
                  {item.notificationsSent > 0 && (
                    <div className="text-xs text-green-600 mt-1">
                      ✓ {item.notificationsSent} notification{item.notificationsSent !== 1 ? 's' : ''} sent
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Completed Reminders Column */}
        <div>
          <h2 className="text-base sm:text-lg font-semibold mb-3">Completed Reminders</h2>
          <div className="space-y-2">
            {sentItems.length === 0 ? (
              <p className="text-gray-500 p-3 bg-gray-50 rounded text-sm">No completed reminders</p>
            ) : (
              sentItems.map(item => (
                <div key={item.id} className="border rounded bg-gray-50 p-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm sm:text-base">{item.discipleName}</h3>
                    <div className="text-xs text-gray-500 text-right">
                      <div>Completed: {item.completedAt?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || 'N/A'}</div>
                      <div>Due: {item.scheduledFor?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Mentor: {item.mentorName || item.mentorId || 'Unknown'}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                    <span className={`font-semibold ${
                      item.reminderStatus === 'completed' ? 'text-green-600' : 
                      item.reminderStatus === 'cancelled' ? 'text-red-600' : 
                      item.reminderStatus === 'superseded' ? 'text-orange-600' :
                      'text-blue-600'
                    }`}>
                      {item.reminderStatus}
                    </span>
                    <span>•</span>
                    <span>{item.queueStatus}</span>
                    {item.completionReason && (
                      <>
                        <span>•</span>
                        <span>{item.completionReason.replace(/_/g, ' ')}</span>
                      </>
                    )}
                  </div>
                  {item.messageSent && (
                    <div className="text-xs text-green-600 mt-1">
                      ✓ Message sent via {item.messageType || 'unknown method'}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReminderQueueTester;